import { getApiBaseUrl } from '../../config/apiConfig';
import { GameTableConfig, ServerGameTableConfig, normalizeGameTableConfig } from '../engine/GameTableConfig';
import { resolveCanonicalGameType, getAliasesForGameType } from '../games/GameModuleManager';

export function normalizeRulesetGameType(rawGameType?: string | null): string {
  return resolveCanonicalGameType(rawGameType);
}

class RulesetConfigService {
  private cache: Map<string, GameTableConfig> = new Map();
  private fetchPromises: Map<string, Promise<GameTableConfig | null>> = new Map();

  public getCachedConfig(gameType: string): GameTableConfig | null {
    if (!gameType) return null;
    const raw = gameType.trim().toUpperCase();
    const normalized = normalizeRulesetGameType(gameType);
    return this.cache.get(raw) ?? this.cache.get(normalized) ?? null;
  }

  public setCachedConfig(gameType: string, config: GameTableConfig): void {
    if (!gameType || !config) return;
    const raw = gameType.trim().toUpperCase();
    const normalized = normalizeRulesetGameType(gameType);
    this.cache.set(raw, config);
    this.cache.set(normalized, config);

    // Dynamically populate all known aliases for this game module
    const aliases = getAliasesForGameType(normalized);
    for (const alias of aliases) {
      this.cache.set(alias.toUpperCase(), config);
    }
  }

  public async fetchRulesetConfig(gameType: string): Promise<GameTableConfig | null> {
    if (!gameType) return null;
    const raw = gameType.trim().toUpperCase();
    const normalized = normalizeRulesetGameType(gameType);

    if (this.cache.has(raw)) return this.cache.get(raw)!;
    if (this.cache.has(normalized)) return this.cache.get(normalized)!;

    if (this.fetchPromises.has(normalized)) {
      return this.fetchPromises.get(normalized)!;
    }

    const fetchPromise = (async () => {
      try {
        const baseUrl = getApiBaseUrl();
        const url = `${baseUrl}/api/arena/rulesets/${encodeURIComponent(normalized)}/config`;
        const res = await fetch(url, { method: 'GET', headers: { 'Accept': 'application/json' } });
        if (!res.ok) return null;
        const data: ServerGameTableConfig = await res.json();
        const normalizedConfig = normalizeGameTableConfig(data);
        if (normalizedConfig) {
          this.setCachedConfig(raw, normalizedConfig);
          this.setCachedConfig(normalized, normalizedConfig);
          return normalizedConfig;
        }
        return null;
      } catch {
        return null;
      } finally {
        this.fetchPromises.delete(normalized);
      }
    })();

    this.fetchPromises.set(normalized, fetchPromise);
    return fetchPromise;
  }

  public async fetchAllRulesetConfigs(): Promise<Record<string, GameTableConfig>> {
    try {
      const baseUrl = getApiBaseUrl();
      const url = `${baseUrl}/api/arena/rulesets`;
      const res = await fetch(url, { method: 'GET', headers: { 'Accept': 'application/json' } });
      if (!res.ok) return {};
      const data: Record<string, ServerGameTableConfig> = await res.json();
      const result: Record<string, GameTableConfig> = {};
      for (const [key, rawCfg] of Object.entries(data)) {
        const normalized = normalizeGameTableConfig(rawCfg);
        if (normalized) {
          this.setCachedConfig(key, normalized);
          result[key.toUpperCase()] = normalized;
        }
      }
      return result;
    } catch {
      return {};
    }
  }
}

export const rulesetConfigService = new RulesetConfigService();
